import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { collection, query, orderBy, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

// Cache configuration
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const CACHE_KEY_PREFIX = 'jobData_';

// In-memory cache for recent queries
const queryCache = new Map();

// Helper to generate cache key from filters
const generateCacheKey = (filters, userLocation) => {
  const key = {
    search: filters.searchTerm || '',
    location: filters.locationFilter || '',
    employmentType: filters.employmentTypeFilter || 'all',
    source: filters.sourceFilter || 'all',
    role: filters.roleFilter || 'all',
    sortBy: filters.sortBy || 'distance', // FIXED: Include sortBy in cache key
    distanceFilter: filters.distanceFilter || 0, // FIXED: Include distanceFilter in cache key
    userLat: userLocation?.latitude || null,
    userLng: userLocation?.longitude || null
  };
  return CACHE_KEY_PREFIX + JSON.stringify(key);
};

// Helper to check if cache is valid
const isCacheValid = (timestamp) => {
  return Date.now() - timestamp < CACHE_DURATION;
};

// Helper function to calculate distance between two points
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3959; // Radius of the Earth in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  return distance;
};

// Helper function to sort jobs
const sortJobs = (jobs, sortBy) => {
  return [...jobs].sort((a, b) => {
    switch (sortBy) {
      case 'distance':
        if (a.distance === null && b.distance === null) return 0;
        if (a.distance === null) return 1;
        if (b.distance === null) return -1;
        return a.distance - b.distance;
      
      case 'newest':
        // FIXED: Improved date extraction to prioritize postedAt
        const aDate = (() => {
          if (a.postedAt) {
            if (a.postedAt.seconds) return a.postedAt.seconds;
            if (a.postedAt instanceof Date) return a.postedAt.getTime() / 1000;
            return new Date(a.postedAt).getTime() / 1000;
          }
          if (a.scrapedAt?.seconds) return a.scrapedAt.seconds;
          return 0;
        })();
        
        const bDate = (() => {
          if (b.postedAt) {
            if (b.postedAt.seconds) return b.postedAt.seconds;
            if (b.postedAt instanceof Date) return b.postedAt.getTime() / 1000;
            return new Date(b.postedAt).getTime() / 1000;
          }
          if (b.scrapedAt?.seconds) return b.scrapedAt.seconds;
          return 0;
        })();
        
        return bDate - aDate; // Newest first
      
      case 'oldest':
        // FIXED: Same improved logic for oldest
        const aDateOld = (() => {
          if (a.postedAt) {
            if (a.postedAt.seconds) return a.postedAt.seconds;
            if (a.postedAt instanceof Date) return a.postedAt.getTime() / 1000;
            return new Date(a.postedAt).getTime() / 1000;
          }
          if (a.scrapedAt?.seconds) return a.scrapedAt.seconds;
          return 0;
        })();
        
        const bDateOld = (() => {
          if (b.postedAt) {
            if (b.postedAt.seconds) return b.postedAt.seconds;
            if (b.postedAt instanceof Date) return b.postedAt.getTime() / 1000;
            return new Date(b.postedAt).getTime() / 1000;
          }
          if (b.scrapedAt?.seconds) return b.scrapedAt.seconds;
          return 0;
        })();
        
        return aDateOld - bDateOld; // Oldest first
      
      case 'location':
        const aLocation = a.location || '';
        const bLocation = b.location || '';
        return aLocation.localeCompare(bLocation);
      
      default:
        return 0;
    }
  });
};

export const useJobData = (userLocation) => {
  // State variables
  const [jobs, setJobs] = useState([]);
  const [totalJobs, setTotalJobs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [employmentTypeFilter, setEmploymentTypeFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sortBy, setSortBy] = useState('distance');
  const [distanceFilter, setDistanceFilter] = useState(0);
  
  // Cache and batching state
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [cacheHits, setCacheHits] = useState(0);
  const fetchTimeoutRef = useRef(null);
  const lastFiltersRef = useRef(null);

  // Memoized filter hash to detect actual changes
  const filterHash = useMemo(() => {
    return JSON.stringify({
      searchTerm,
      locationFilter,
      employmentTypeFilter,
      sourceFilter,
      roleFilter,
      sortBy, // FIXED: Include sortBy in filter hash
      distanceFilter, // FIXED: Include distanceFilter in filter hash
      userLocation: userLocation ? `${userLocation.latitude},${userLocation.longitude}` : null
    });
  }, [searchTerm, locationFilter, employmentTypeFilter, sourceFilter, roleFilter, sortBy, distanceFilter, userLocation]);

  // Client-side filtering function
  const applyClientSideFilters = useCallback((jobs, filters) => {
    if (!Array.isArray(jobs)) {
      console.warn('applyClientSideFilters: jobs is not an array:', typeof jobs);
      return [];
    }
    
    return jobs.filter(job => {
      try {
        // Search filter
        if (filters.searchTerm && filters.searchTerm.trim().length > 0) {
          const searchLower = filters.searchTerm.toLowerCase();
          const searchableText = [
            job.title,
            job.description,
            job.location,
            job.practiceInfo,
            job.practiceNameAtPosting,
            job.practiceName,
            job.postcode,
            job.employmentType,
            job.salary
          ].filter(Boolean).join(' ').toLowerCase();
          
          if (!searchableText.includes(searchLower)) {
            return false;
          }
        }

        // Location filter
        if (filters.locationFilter && filters.locationFilter.trim().length > 0) {
          const locationLower = filters.locationFilter.toLowerCase();
          const locationText = [
            job.location,
            job.postcode,
            job.practiceInfo,
            job.practiceName
          ].filter(Boolean).join(' ').toLowerCase();
          
          if (!locationText.includes(locationLower)) {
            return false;
          }
        }

        // Employment type filter
        if (filters.employmentTypeFilter !== 'all') {
          if (job.employmentType !== filters.employmentTypeFilter) {
            return false;
          }
        }

        // CRITICAL FIX: Enhanced role filter with multiple field checking
        if (filters.roleFilter !== 'all') {
          // CRITICAL: Check multiple possible role fields
          const jobRole = job.role || job.jobType || job.title || job.category || '';
          
          if (!jobRole) {
            console.warn(`Job ${job.id} has no role field:`, {
              role: job.role,
              jobType: job.jobType,
              title: job.title,
              category: job.category
            });
            return false;
          }
          
          // CRITICAL FIX: More robust role matching
          const jobRoleStr = String(jobRole).trim().toLowerCase();
          const filterRoleStr = String(filters.roleFilter).trim().toLowerCase();
          
          // Enhanced role mapping for better matching
          const roleMatches = (jobRoleStr, filterRole) => {
            // Direct match
            if (jobRoleStr === filterRole) return true;
            
            // Handle different role formats
            const roleMapping = {
              'hygienist/therapist': ['hygienist', 'therapist', 'dental hygienist', 'dental therapist', 'hygienist/therapist'],
              'locum dentist': ['dentist', 'locum', 'locum dentist', 'dental surgeon'],
              'dental nurse': ['nurse', 'dental nurse', 'dental nursing assistant'],
              'practice manager': ['manager', 'practice manager', 'practice management'],
              'receptionist': ['receptionist', 'reception', 'front desk']
            };
            
            const matchingTerms = roleMapping[filterRole] || [filterRole];
            
            return matchingTerms.some(term => 
              jobRoleStr.includes(term.toLowerCase()) || 
              term.toLowerCase().includes(jobRoleStr)
            );
          };
          
          const isMatch = roleMatches(jobRoleStr, filterRoleStr);
          
          if (!isMatch) {
            return false;
          } 
        }

        return true;
      } catch (filterError) {
        console.error('Error in job filter:', filterError, job);
        return false; // Exclude jobs that cause errors
      }
    });
  }, []);

  // Distance processing function
  const processJobsWithDistance = useCallback((jobs, userLoc, sorting, distanceFilterValue) => {
    // Calculate distances
    const jobsWithDistance = jobs.map(job => {
      if (userLoc && job.latitude && job.longitude) {
        const jobLat = parseFloat(job.latitude);
        const jobLng = parseFloat(job.longitude);
        
        if (!isNaN(jobLat) && !isNaN(jobLng)) {
          const distance = calculateDistance(
            userLoc.latitude,
            userLoc.longitude,
            jobLat,
            jobLng
          );
          return { ...job, distance };
        }
      }
      return { ...job, distance: null };
    });

    // FIXED: Apply distance filtering correctly
    const filteredByDistance = distanceFilterValue > 0 
      ? jobsWithDistance.filter(job => {
          // FIXED: Only filter out jobs that have a distance AND are beyond the filter
          // Keep jobs without distance (distance === null) so users can still see them
          return job.distance === null || job.distance <= distanceFilterValue;
        })
      : jobsWithDistance;

    // Apply sorting
    return sortJobs(filteredByDistance, sorting);
  }, []);

  // Main fetch jobs function
  const fetchJobs = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      
      // Generate cache key
      const cacheKey = generateCacheKey({
        searchTerm,
        locationFilter,
        employmentTypeFilter,
        sourceFilter,
        roleFilter,
        sortBy,
        distanceFilter
      }, userLocation);

      // Check cache first (unless force refresh)
      if (!forceRefresh && queryCache.has(cacheKey)) {
        const cached = queryCache.get(cacheKey);
        if (isCacheValid(cached.timestamp)) {
          setJobs(cached.jobs);
          setTotalJobs(cached.totalJobs);
          setLoading(false);
          setCacheHits(prev => prev + 1);
          return;
        } else {
          queryCache.delete(cacheKey);
        }
      }

      // CRITICAL FIX: Enhanced rate limiting - allow role filter changes to bypass rate limits
      const timeSinceLastFetch = Date.now() - lastFetchTime;
      const isRoleFilterChange = roleFilter && roleFilter !== 'all';
      const isInitialLoad = lastFetchTime === 0;
      
      // Different rate limits for different scenarios
      if (!forceRefresh && !isInitialLoad) {
        if (isRoleFilterChange) {
          // CRITICAL FIX: Reduce rate limit for role filter changes to 300ms (was 500ms)
          if (timeSinceLastFetch < 300) {
            setLoading(false);
            return;
          }
        } else {
          // For other changes, use normal rate limit (2000ms)
          if (timeSinceLastFetch < 2000) {
            setLoading(false);
            return;
          }
        }
      }

      // Query both collections
      let rssJobs = [];
      let practiceJobs = [];

      // Fetch from rss_jobs collection
      if (sourceFilter !== 'practice') {
        let rssQuery = query(collection(db, 'rss_jobs'), orderBy('scrapedAt', 'desc'));
        
        if (sourceFilter === 'rss') {
          rssQuery = query(rssQuery, where('source', 'in', ['rss', 'hygienist_therapist', 'locum_dentist', 'dental_nurse']));
        }
        
        const rssSnapshot = await getDocs(rssQuery);
        rssJobs = rssSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          sourceType: 'rss'
        }));
      }

      // Fetch from job_listings collection
      if (sourceFilter !== 'rss') {
        // FIXED: Add where clause to filter out expired practice jobs
        let practiceQuery = query(
          collection(db, 'job_listings'), 
          where('expiresAt', '>', new Date()), // Only get jobs that haven't expired
          orderBy('expiresAt', 'asc'),
          orderBy('postedAt', 'desc')
        );
        
        const practiceSnapshot = await getDocs(practiceQuery);
        practiceJobs = practiceSnapshot.docs.map(doc => {
          const data = doc.data();
          
          // Extract location coordinates
          let latitude = null;
          let longitude = null;
          
          if (data.practiceLocation && typeof data.practiceLocation === 'object') {
            latitude = data.practiceLocation.latitude;
            longitude = data.practiceLocation.longitude;
          }
          
          if (!latitude && !longitude) {
            latitude = data.latitude;
            longitude = data.longitude;
          }
          
          if (latitude !== null && longitude !== null) {
            latitude = parseFloat(latitude);
            longitude = parseFloat(longitude);
            
            if (isNaN(latitude) || isNaN(longitude)) {
              console.warn(`Invalid coordinates for practice job ${doc.id}:`, { latitude, longitude });
              latitude = null;
              longitude = null;
            }
          }
          
          return {
            id: doc.id,
            ...data,
            sourceType: 'practice',
            source: 'practice',
            latitude: latitude,
            longitude: longitude,
            location: data.location || `${data.practiceInfo?.name || 'Practice'}, ${data.postcode || ''}`.trim().replace(/,$/, ''),
            role: data.role || (() => {
              const title = (data.title || '').toLowerCase();
              if (title.includes('hygienist') || title.includes('therapist')) {
                return 'Hygienist/Therapist';
              }
              if (title.includes('dentist') || title.includes('locum')) {
                return 'Locum Dentist';
              }
              if (title.includes('nurse')) {
                return 'Dental Nurse';
              }
              return 'Hygienist/Therapist';
            })(),
            employmentType: data.employmentType || 'Contract'
          };
        });
      }

      // Combine all jobs
      let allRawJobs = [...rssJobs, ...practiceJobs];

      // Apply client-side filters (NOT including distance)
      const filteredJobs = applyClientSideFilters(allRawJobs, {
        searchTerm,
        locationFilter,
        employmentTypeFilter,
        roleFilter
      });

      // Apply distance calculation and sorting
      const processedJobs = processJobsWithDistance(filteredJobs, userLocation, sortBy, distanceFilter);

      // CRITICAL FIX: Update state atomically to prevent race conditions
      const cacheData = {
        jobs: processedJobs,
        totalJobs: processedJobs.length,
        timestamp: Date.now()
      };
      
      queryCache.set(cacheKey, cacheData);
      
      // CRITICAL: Set all states in the correct order
      setTotalJobs(processedJobs.length);
      setJobs(processedJobs);
      setLastFetchTime(Date.now());
      setLoading(false);
      setError(null);

    } catch (err) {
      console.error('Error in fetchJobs:', err);
      setError('Failed to load jobs. Please try again.');
      setLoading(false);
    }
  }, [searchTerm, locationFilter, employmentTypeFilter, sourceFilter, roleFilter, userLocation, sortBy, distanceFilter, lastFetchTime, cacheHits, applyClientSideFilters, processJobsWithDistance]);

  // Batched filter change handler
  const batchedFilterChange = useCallback(() => {
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    fetchTimeoutRef.current = setTimeout(() => {
      const currentFilterHash = filterHash;
      
      if (lastFiltersRef.current !== currentFilterHash) {
        lastFiltersRef.current = currentFilterHash;
        fetchJobs();
      }
    }, 300);
  }, [filterHash, fetchJobs]);

  // Use batched filter changes
  useEffect(() => {
    if (userLocation) {
      batchedFilterChange();
    }
  }, [filterHash, userLocation, batchedFilterChange]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, []);

  // Refresh with cache clearing
  const refreshJobs = useCallback(() => {
    queryCache.clear();
    setCacheHits(0);
    setRefreshing(true);
    
    fetchJobs(true).finally(() => {
      setRefreshing(false);
    });
  }, [fetchJobs]);

  // Cache statistics for debugging
  const cacheStats = useMemo(() => ({
    cacheSize: queryCache.size,
    cacheHits,
    lastFetchTime: new Date(lastFetchTime).toLocaleTimeString()
  }), [cacheHits, lastFetchTime]);

  // Clear filters function
  const clearFilters = useCallback(() => {
    queryCache.clear();
    setCacheHits(0);
    
    setSearchTerm('');
    setLocationFilter('');
    setEmploymentTypeFilter('all');
    setSourceFilter('all');
    setRoleFilter('all'); // This should set role to 'all'
    setSortBy('distance');
    setDistanceFilter(0); // FIXED: Add missing distance filter reset
    
  }, []);

  return {
    jobs,
    loading,
    error,
    totalJobs,
    refreshing,
    searchTerm,
    locationFilter,
    employmentTypeFilter,
    sourceFilter,
    sortBy,
    roleFilter,
    distanceFilter, // FIXED: Add missing distanceFilter to return object
    setSearchTerm,
    setLocationFilter,
    setEmploymentTypeFilter,
    setSourceFilter,
    setSortBy,
    setRoleFilter,
    setDistanceFilter, // FIXED: Add missing setDistanceFilter to return object
    cacheStats,
    refreshJobs,
    clearFilters
  };
};
